import React, { useMemo, useState } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { useBetterAuthConvexAuth } from '../../hooks/useAppAuth';

interface UpdateDetailsModalProps {
  onClose: () => void;
  onUpdate: () => void;
  allowClose?: boolean;
}

type VerificationChannel = 'secondary_email' | 'mobile';
type VerificationState = Record<VerificationChannel, boolean>;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_REGEX = /^(\+44\s?7\d{3}|\(?07\d{3}\)?)\s?\d{3}\s?\d{3}$/;

const UpdateDetailsModal: React.FC<UpdateDetailsModalProps> = ({
  onClose,
  onUpdate,
  allowClose = true,
}) => {
  const { convexUser } = useBetterAuthConvexAuth();

  const verificationWindow = useMemo(() => {
    const now = Date.now();
    const hasMobile = Boolean(convexUser?.mobileNumber?.trim());
    const hasSecondaryEmail = Boolean(convexUser?.email2?.trim());
    const mobileExpired = hasMobile && (
      !convexUser?.mobileVerified ||
      !convexUser.mobileVerifiedTillDate ||
      convexUser.mobileVerifiedTillDate <= now
    );

    const emailExpired = hasSecondaryEmail && (
      !convexUser?.email2Verified ||
      !convexUser.email2VerifiedTillDate ||
      convexUser.email2VerifiedTillDate <= now
    );
    const needsContactMethod = !hasMobile && !hasSecondaryEmail;

    return {
      mobileExpired,
      emailExpired,
      needsContactMethod,
      needsVerification: mobileExpired || emailExpired || needsContactMethod,
    };
  }, [
    convexUser?.mobileNumber,
    convexUser?.mobileVerified,
    convexUser?.mobileVerifiedTillDate,
    convexUser?.email2,
    convexUser?.email2Verified,
    convexUser?.email2VerifiedTillDate,
  ]);

  const [step, setStep] = useState<'details' | 'verify'>('details');
  const [formData, setFormData] = useState({
    email2: convexUser?.email2 || '',
    mobile_number: convexUser?.mobileNumber || '',
  });
  const [verifyType, setVerifyType] = useState<VerificationChannel | null>(null);
  const [verificationNeeded, setVerificationNeeded] = useState<VerificationState>({
    secondary_email: false,
    mobile: false,
  });
  const [verificationDone, setVerificationDone] = useState<VerificationState>({
    secondary_email: false,
    mobile: false,
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const updateProfile = useMutation(api.users.updateMyProfile);
  const sendVerificationCode = useMutation(api.users.sendMyVerificationCode);
  const verifyCode = useMutation(api.users.verifyMyCode);
  const markVerified = useMutation(api.myFunctions.markDetailsVerifiedAfterLogin);

  const getChannelLabel = (channel: VerificationChannel | null) => {
    if (channel === 'secondary_email') return 'secondary email';
    if (channel === 'mobile') return 'mobile number';
    return 'contact method';
  };

  const handleSubmitDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const nextEmail2 = formData.email2.trim();
      const nextMobile = formData.mobile_number.trim();

      if (verificationWindow.emailExpired && !nextEmail2) {
        setError('Secondary email is required for verification.');
        setLoading(false);
        return;
      }
      if (verificationWindow.mobileExpired && !nextMobile) {
        setError('Mobile number is required for verification.');
        setLoading(false);
        return;
      }
      if (verificationWindow.needsContactMethod && !nextEmail2 && !nextMobile) {
        setError('Please provide a secondary email or mobile number so we can contact you.');
        setLoading(false);
        return;
      }
      if (nextEmail2 && !EMAIL_REGEX.test(nextEmail2)) {
        setError('Enter a valid secondary email.');
        setLoading(false);
        return;
      }
      if (nextMobile && !MOBILE_REGEX.test(nextMobile)) {
        setError('Enter a valid mobile number.');
        setLoading(false);
        return;
      }

      const result = await updateProfile({
        email2: nextEmail2 || undefined,
        mobileNumber: nextMobile || undefined,
      });

      if (!result.success) {
        setError(result.error || 'Failed to update details');
        setLoading(false);
        return;
      }

      const nextVerification: VerificationState = {
        secondary_email: verificationWindow.emailExpired || (
          verificationWindow.needsContactMethod && Boolean(nextEmail2)
        ),
        mobile: verificationWindow.mobileExpired || (
          verificationWindow.needsContactMethod && Boolean(nextMobile)
        ),
      };

      if (nextVerification.secondary_email || nextVerification.mobile) {
        setVerificationNeeded(nextVerification);
        setVerificationDone({ secondary_email: false, mobile: false });
        setStep('verify');

        const firstChannel: VerificationChannel = nextVerification.secondary_email
          ? 'secondary_email'
          : 'mobile';
        setVerifyType(firstChannel);

        const deliveries: VerificationChannel[] = [];
        if (nextVerification.secondary_email) deliveries.push('secondary_email');
        if (nextVerification.mobile) deliveries.push('mobile');

        for (const delivery of deliveries) {
          const codeResult = await sendVerificationCode({
            type: delivery,
            clientSource: 'web',
          });
          if (!codeResult.success) {
            setError(codeResult.error || `Failed to send ${getChannelLabel(delivery)} verification code`);
            setLoading(false);
            return;
          }
        }
      } else {
        onUpdate();
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update details';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!verifyType) {
        setError('Verification type not set');
        return;
      }

      const result = await verifyCode({
        type: verifyType,
        code: verificationCode.trim(),
      });

      if (!convexUser?._id) {
        setError('User not found');
        return;
      }

      if (result.success) {
        const verifiedChannel = verifyType;
        await markVerified({
          userId: convexUser._id,
          setMobileVerified: verifiedChannel === 'mobile',
          setEmailVerified: verifiedChannel === 'secondary_email',
        });

        const nextVerificationDone = {
          ...verificationDone,
          [verifiedChannel]: true,
        };
        setVerificationDone(nextVerificationDone);

        const nextChannel = verificationNeeded.secondary_email && !nextVerificationDone.secondary_email
          ? 'secondary_email'
          : verificationNeeded.mobile && !nextVerificationDone.mobile
            ? 'mobile'
            : null;

        if (nextChannel) {
          setVerifyType(nextChannel);
          setVerificationCode('');
          setError('');
          return;
        }

        window.location.assign('/dashboard');
        return;
      }

      setError(result.error ?? 'Invalid verification code');

    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Invalid verification code';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setLoading(true);

    try {
      if (!verifyType) {
        setError('Verification type not set');
        return;
      }

      const result = await sendVerificationCode({ type: verifyType, clientSource: 'web' });
      if (result.success) {
        setError('Verification code sent!');
      } else {
        setError(result.error || 'Failed to send code');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to send code';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <h2 className="text-xl sm:text-2xl font-medium font-sans mb-3 sm:mb-4">
          {step === 'details' ? 'Verify Your Contact Details' : 'Verify Your Contact Information'}
        </h2>

        {step === 'details' ? (
          <>
            <p className="text-gray-600 mb-6">
              We need to verify your contact details before you continue.
              {!verificationWindow.emailExpired && verificationWindow.needsContactMethod
                ? ' Please add a secondary email or mobile number.'
                : ''}
            </p>

            <form onSubmit={handleSubmitDetails} className="space-y-4">
              {(verificationWindow.emailExpired || verificationWindow.needsContactMethod) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Secondary Email {verificationWindow.needsContactMethod && (
                      <span className="font-normal text-gray-500">(optional)</span>
                    )}
                  </label>
                  <input
                    type="email"
                    value={formData.email2}
                    onChange={(e) => setFormData({ ...formData, email2: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-shade-green-500"
                    placeholder="secondary@email.com"
                    required={verificationWindow.emailExpired}
                  />
                </div>
              )}

              {(verificationWindow.mobileExpired || verificationWindow.needsContactMethod) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mobile Number {verificationWindow.needsContactMethod && (
                      <span className="font-normal text-gray-500">(optional)</span>
                    )}
                  </label>
                  <input
                    type="tel"
                    value={formData.mobile_number}
                    onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-shade-green-500"
                    placeholder="07700123456"
                    required={verificationWindow.mobileExpired}
                  />
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4">
                {allowClose && (
                  <button
                    type="button"
                    onClick={onClose}
                    className="w-full sm:w-auto px-4 py-3 sm:py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-[#16A34A] text-white rounded-md hover:bg-shade-green-700"
                  disabled={loading}
                >
                  {loading ? 'Updating...' : 'Continue'}
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <p className="text-gray-600 mb-6">
              We sent a 4-digit verification code to your {getChannelLabel(verifyType)}.
            </p>

            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  className="w-full text-center text-2xl tracking-widest px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-shade-green-500"
                  placeholder="0000"
                  maxLength={4}
                  pattern="\d{4}"
                  required
                />
              </div>

              {error && (
                <div
                  className={`${error.includes('sent')
                    ? 'bg-green-50 border-green-200 text-green-700'
                    : 'bg-red-50 border-red-200 text-red-700'
                    } border px-4 py-3 rounded-md text-sm`}
                >
                  {error}
                </div>
              )}

              <div className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={handleResendCode}
                  className="text-shade-green-600 hover:text-shade-green-700 text-sm"
                  disabled={loading}
                >
                  Resend Code
                </button>

                <div className="space-x-3">
                  <button
                    type="button"
                    onClick={() => setStep('details')}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    disabled={loading}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#16A34A] text-white rounded-md hover:bg-shade-green-700"
                    disabled={loading || verificationCode.length !== 4}
                  >
                    {loading ? 'Verifying...' : 'Verify'}
                  </button>
                </div>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default UpdateDetailsModal;
